import { observable, action, computed, autorun, makeObservable } from "mobx";
import WebPushApi from "./api/webpush";
import { LS_KEYS } from "../../consts";
import callWindowFunction from "../../utils/callWindowFunction";

class ClientStorageStore {
  constructor(rootStore) {
    makeObservable(this, {
      webPushToken: observable,
      subscribedUser: observable,
      isUserChanged: computed,
      itsTimeForInvite: computed,
      setInviteTimestamp: action,
      callbackWebpush: action,
    });

    this.rootStore = rootStore;
    this.WebPushApi = new WebPushApi(rootStore.feedSettings.webPushApi);

    autorun(
      (reaction) => {
        if (this.shouldRegistrationRemove()) {
          this.unregisterToken();
          const value = {
            step: 0,
            timestamp: new Date(),
            id: this.rootStore.profile.userId,
          };
          callWindowFunction("putValue", [LS_KEYS.INVITE_TIME, value]);
          this.callbackInvite({ value });
          reaction.dispose();
        }
      },
      { delay: 2500 }
    );
  }

  webPushToken = null;

  subscribedUser = null;

  inviteTimestamp = null;

  invitedUser = null;

  inviteStep = null;

  needToShowInvite = false;

  setNeedToShowInvite = (value) => {
    this.needToShowInvite = value;
  };

  unregisterToken = () => {
    const token = this.webPushToken;
    const value = { token: null, id: null };
    callWindowFunction("putValue", [LS_KEYS.WEBPUSH, value]);
    this.callbackWebpush({ value });
    const unregisterRetry = async (n, finaly) => {
      try {
        return await this.WebPushApi.unregister(token);
      } catch (err) {
        return n === 1
          ? finaly()
          : setTimeout(() => unregisterRetry(n - 1, finaly), 2000);
      }
    };
    unregisterRetry(3, this.unregisterServiceWorker);
  };

  get isUserChanged() {
    return this.subscribedUser !== this.rootStore.profile.userId;
  }

  get itsTimeForInvite() {
    if (this.needToShowInvite) {
      return true;
    }

    if (this.invitedUser !== this.rootStore.profile.userId) {
      const value = { step: null, timestamp: null, id: null };
      callWindowFunction("putValue", [LS_KEYS.INVITE_TIME, value]);
      this.callbackInvite({ value });

      return true;
    }
    const minDate = new Date(this.inviteTimestamp);
    minDate.setDate(minDate.getDate() + this.inviteStep);

    return new Date() > minDate;
  }

  unregisterServiceWorker = () => {
    document
      .getElementById("web-push-frame")
      .contentWindow.postMessage(
        "unregisterServiceWorker",
        `${window.location.protocol}${this.rootStore.feedSettings.webPushFrame}`
      );
  };

  shouldRegistrationRemove = () =>
    this.subscribedUser !== null &&
    this.subscribedUser !== this.rootStore.profile.userId;

  setInviteTimestamp = () => {
    let step = this.inviteStep + 2;
    step = step > 8 ? 8 : step;
    const value = {
      step,
      timestamp: new Date(),
      id: this.rootStore.profile.userId,
    };
    window.putValue(LS_KEYS.INVITE_TIME, value);
    this.callbackInvite({ value });
  };

  callbackWebpush = ({ value }) => {
    if (!value) {
      this.webPushToken = null;
      this.subscribedUser = this.rootStore.profile.userId;
      return;
    }

    this.webPushToken = value.token || null;
    this.subscribedUser = value.id || this.rootStore.profile.userId;
  };

  callbackIsActive = ({ value }) => {
    if (value && value === this.rootStore.profile.userId) {
      this.rootStore.feedSettings.setActivation(value);
    } else {
      this.rootStore.feedSettings.activateFeedForUser();
    }
  };

  callbackInvite = ({ value }) => {
    if (!value) {
      this.inviteTimestamp = new Date();
      this.invitedUser = this.rootStore.profile.userId;
      this.inviteStep = 0;
      return;
    }

    this.inviteTimestamp = value.timestamp || null;
    this.invitedUser = value.id || null;
    this.inviteStep = value.step || null;
  };
}

export default ClientStorageStore;
